import { DismissableLayer } from "@radix-ui/react-dismissable-layer";
import { AnimatePresence, LazyMotion, m } from "framer-motion";
import React, { createContext, useCallback, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { createPortal } from "react-dom";
import { styled } from "styled-components";
import { mountAnimation, unmountAnimation } from "../../components/BottomDrawer/styles";
import { Overlay } from "../../components/Overlay";
import { useIsomorphicEffect } from "../../hooks";
import {
  animationHandler,
  animationMap,
  animationVariants,
  appearAnimation,
  disappearAnimation,
} from "../../util/animationToolkit";
import getPortalRoot from "../../util/getPortalRoot";
import { ModalContainer } from "./styles";
import { Handler } from "./types";

const DomMax = () => import("./motionDomMax").then((mod) => mod.default);
const DomAnimation = () => import("./motionDomAnimation").then((mod) => mod.default);

interface ModalsContext {
  isOpen: boolean;
  nodeId: string;
  modalNode: React.ReactNode;
  updateModalNode: (nodeId: string, node: React.ReactNode) => void;
  // React.Dispatch<React.SetStateAction<React.ReactNode>>;
  onPresent: (node: React.ReactNode, newNodeId: string, closeOverlayClick: boolean) => void;
  onDismiss: Handler;
}

export const StyledModalWrapper = styled(m.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${({ theme }) => theme.zIndices.modal - 1};
  // backdrop-filter: blur(6px);
  will-change: opacity;
  opacity: 0;
  &.appear {
    animation: ${appearAnimation} 0.3s ease-in-out forwards;
    ${ModalContainer} {
      animation: ${mountAnimation} 0.3s ease-in-out forwards;
      ${({ theme }) => theme.mediaQueries.md} {
        animation: none;
      }
    }
  }
  &.disappear {
    animation: ${disappearAnimation} 0.3s ease-in-out forwards;
    ${ModalContainer} {
      animation: ${unmountAnimation} 0.3s ease-in-out forwards;
      ${({ theme }) => theme.mediaQueries.md} {
        animation: none;
      }
    }
  }
` as typeof m.div;

export const Context = createContext<ModalsContext>({
  isOpen: false,
  nodeId: "",
  modalNode: null,
  updateModalNode: () => null,
  onPresent: () => null,
  onDismiss: () => null,
});

const ModalProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  // const [isOpen, setIsOpen] = useState(false);
  const [modalNodes, setModalNodes] = useState<{ id: string, node: React.ReactNode, closable: boolean}[]>([]);
  // const [nodeId, setNodeId] = useState("");
  // const [closeOnOverlayClick, setCloseOnOverlayClick] = useState(true);
  const animationRef = useRef<HTMLDivElement>(null);
  const isOpen = useMemo(() => modalNodes.length > 0, [modalNodes])
  const topNode = useMemo(() => modalNodes.length ? modalNodes[modalNodes.length - 1] : undefined, [modalNodes])

  useIsomorphicEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    setViewportHeight();
    window.addEventListener("resize", setViewportHeight);
    return () => window.removeEventListener("resize", setViewportHeight);
  }, []);

  const handlePresent = useCallback((node: React.ReactNode, newNodeId: string, closeOverlayClick: boolean) => {
    // setIsOpen(true);
    setModalNodes(nodes => [...nodes, { id: newNodeId, node, closable: closeOverlayClick }])
    // setModalNode(node);
    // setNodeId(newNodeId);
    // setCloseOnOverlayClick(closeOverlayClick);
  }, []);

  const handleDismiss = useCallback(() => {
    // setIsOpen(false);
    setModalNodes(nodes => [...nodes.slice(0, -1)])
    // setModalNode(undefined);
    // setNodeId("");
    // setCloseOnOverlayClick(true);
  }, []);

  const handleOverlayDismiss = useCallback((e: any) => {
    if (topNode?.closable) {
      handleDismiss();
    }
    e.stopPropagation()
  }, [topNode, handleDismiss]);

  const providerValue = useMemo(() => {
    const updateModalNode = (nodeId: string, node: React.ReactNode) => {
      setModalNodes(nodes => {
        const modalNode = nodes.find(n => n.id === nodeId)
        if (modalNode) {
          modalNode.node = node
          return [...nodes]
        }
        return nodes
      })
    }
    
    return { isOpen, nodeId: topNode?.id ?? "", modalNode: topNode?.node, updateModalNode, onPresent: handlePresent, onDismiss: handleDismiss };
  }, [isOpen, topNode, setModalNodes, handlePresent, handleDismiss]);

  const portal = useMemo(() => getPortalRoot(), []);

  return (
    <Context.Provider value={providerValue}>
      {portal &&
        createPortal(
          <LazyMotion features={isMobile ? DomMax : DomAnimation}>
            <AnimatePresence>
              {modalNodes.length > 0 && (
                <DismissableLayer
                  role="dialog"
                  disableOutsidePointerEvents={false}
                  onEscapeKeyDown={handleOverlayDismiss}
                >
                  <StyledModalWrapper
                    ref={animationRef}
                    onAnimationStart={() => animationHandler(animationRef.current)}
                    {...animationMap}
                    variants={animationVariants}
                    transition={{ duration: 0.3 }}
                  >
                    <Overlay onClick={handleOverlayDismiss} />
                    {
                      modalNodes.map(modalNode => 
                        React.isValidElement(modalNode.node) &&
                        React.cloneElement(modalNode.node, {
                          // @ts-ignore
                          onDismiss: handleDismiss,
                        })
                      )
                    }
                  </StyledModalWrapper>
                </DismissableLayer>
              )}
            </AnimatePresence>
          </LazyMotion>
          ,portal
        )}
      {children}
    </Context.Provider>
  );
};

export default ModalProvider;
